import { debounceTime } from "rxjs/operators";

import {
    ChangeDetectionStrategy, Component, DestroyRef, forwardRef,
    OnInit, inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators, FormsModule, ReactiveFormsModule
} from "@angular/forms";
import {
    MatFormField, MatLabel, MatError, MatSuffix
} from "@angular/material/form-field";

import { CommonValidators } from "../../../custom-validators/common-validators";
import { MatInput } from "@angular/material/input";
import { ClearInputDirective } from "../../../forms/directives/clear-input/clear-input.directive";
import { ClearButtonComponent } from "../../../components/shared/clear-button/clear-button.component";
import { EnhancedControlDirective } from "../../../forms/directives/form-control/enhanced-control.directive";
import { ErrorMessageComponent } from "../../../components/error-message/error-message.component";

/**
 * Custom form control that renders a password input with a real-time requirement
 * checklist and a confirm-password input.
 *
 * Each password validator error is displayed simultaneously as a list item,
 * toggling a CSS class as requirements are met in real-time. This is a deliberate
 * UX pattern that intentionally shows **all** active errors at once — not just the
 * first — so users can see every outstanding requirement while they type.
 *
 * **`mgd-error-message` is intentionally NOT used here.** {@link ErrorMessageComponent}
 * only renders the first active error, which is incompatible with this multi-error
 * checklist pattern. This component manages its own error display in its template.
 *
 * @selector mgd-set-password
 */
@Component({
    selector: "mgd-set-password",
    templateUrl: "./set-password-control.component.html",
    styleUrl: "./set-password-control.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => SetPasswordControlComponent),
        multi: true
    }],
    hostDirectives: [
        EnhancedControlDirective
    ],
    imports: [
        MatFormField,
        MatLabel,
        MatInput,
        FormsModule,
        ClearInputDirective,
        ReactiveFormsModule,
        MatError,
        MatSuffix,
        ClearButtonComponent,
        EnhancedControlDirective,
        ErrorMessageComponent
    ]
})
export class SetPasswordControlComponent implements ControlValueAccessor, OnInit {

    readonly passwordFormControl: FormControl = new FormControl("", [
        Validators.required,
        CommonValidators.passwordValidator()
    ]);

    readonly confirmPasswordFormControl: FormControl = new FormControl("", [
        Validators.required,
        CommonValidators.confirmPasswordValidator(this.passwordFormControl)
    ]);

    private readonly _destroyRef = inject(DestroyRef);

    ngOnInit(): void {

        this.passwordFormControl.valueChanges.pipe(
            debounceTime(300),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((value: any) => {

            if (value) {
                this.passwordFormControl.markAsTouched();
                this._emitTouched();
            }

            this.confirmPasswordFormControl.setValue("");
            this.confirmPasswordFormControl.reset();

            if (this.passwordFormControl.valid) {
                this.confirmPasswordFormControl.enable();
            }
            else {
                this.confirmPasswordFormControl.disable();
            }
        });

        this.confirmPasswordFormControl.valueChanges.pipe(
            debounceTime(300),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((value: any) => {

            if (value) {
                this.confirmPasswordFormControl.markAsTouched();
            }

            if (this.confirmPasswordFormControl.valid && this.passwordFormControl.valid) {
                this._emitChanged(this.passwordFormControl.value);
            }
            else {
                this._emitChanged(undefined);
            }
        });
    }

    writeValue(obj: any): void {

        this.passwordFormControl.setValue(obj);
    }

    registerOnChange(fn: any): void {

        this._emitChanged = fn;
    }

    registerOnTouched(fn: any): void {

        this._emitTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {

        if (isDisabled) {
            this.passwordFormControl.disable();
            this.confirmPasswordFormControl.disable();
        }
        else {
            this.passwordFormControl.enable();
            this.confirmPasswordFormControl.enable();
        }
    }

    private _emitChanged: (_: any) => void = () => {};

    private _emitTouched: () => void = () => {};

}
